<template>
    <form-validation v-slot="{ bindings: validationBindings }">
        <modal-dialog
            ref="createOrderModal"
            label="Opdracht aanmaken"
            description="Hier kan je een opdracht aanmaken voor een lead"
            :size="4"
        >
            <flex-container>
                <flex-container>
                    <div>
                        <text-heading>
                            <span>Opdracht aanmaken voor </span>
                            <text-decorator :color="Color.brandPurple">
                                {{ lead.pipeline.name }}
                            </text-decorator>
                        </text-heading>
                        <text-heading sub>
                            {{ lead.assignee.team.name }}
                        </text-heading>
                    </div>

                    <text-description>
                        Het is mogelijk om een opdracht aan te maken gebaseerd op een lead. Hieronder kun je het opdracht type instellen en aan welk team
                        deze wordt toegewezen.
                    </text-description>
                </flex-container>

                <flex-container>
                    <input-group v-model="formData.orderType">
                        <template #label>
                            <input-label>
                                Opdracht type
                            </input-label>
                        </template>
                        <template #input>
                            <select-input :items="availableOrderTypeOptions" />
                        </template>
                    </input-group>
                </flex-container>

                <flex-container>
                    <flex-container :size="5">
                        <input-group
                            v-model="formData.assignedToTeam"
                            :schema="formSchema.assignedToTeam"
                        >
                            <template #label>
                                <input-label>
                                    Toegewezen aan team
                                </input-label>
                            </template>
                            <template #input>
                                <team-select-input
                                    :teams="availableAssignedToTeams"
                                />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container :size="5">
                        <input-group
                            v-model="formData.assignedToUser"
                            :schema="formSchema.assignedToUser"
                        >
                            <template #label>
                                <input-label>
                                    Toegewezen aan gebruiker (optioneel)
                                </input-label>
                            </template>
                            <template #input>
                                <user-select-input
                                    :users="availableAssignedToUsers"
                                    :disabled="!formData.assignedToTeam"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>
            </flex-container>

            <template #footer>
                <dialog-footer>
                    <button-outline @click="redirectToDetail">
                        Annuleren
                    </button-outline>

                    <button-block-confirm :disabled="!validationBindings.isValid.value" @confirmed="onSave($event)">
                        Toevoegen

                        <template #confirmation>
                            Weet je het zeker?
                        </template>
                    </button-block-confirm>
                </dialog-footer>
            </template>
        </modal-dialog>
    </form-validation>
</template>

<script setup lang="ts">
    import {
        ModalDialog,
        ButtonOutline,
        TextDescription,
        TextDecorator,
        Color,
        type ExposedOverlay,
        ButtonBlockConfirm,
        FlexContainer,
        InputGroup,
        InputLabel,
        DialogFooter,
    } from '@keyvue/one-ui';

    import {
        TeamId,
        Lead,
        UserId,
        type TeamSubResource,
        User,
        Team,
        Pipeline,
    } from '@keyvue/one-api-client';

    import {
        createResourceIdentifier,
        OrderAPI,
        OrderType,
    } from '@keyvue/one-api-client-legacy';

    import { computed, type Ref, ref, watch } from 'vue';
    import { object, StringSchema } from 'yup';
    import { v4 as uuid } from 'uuid';
    import UserSelectInput from '@components/Inputs/UserSelectInput.vue';
    import TeamSelectInput from '@components/Inputs/TeamSelectInput.vue';
    import useOverlayAsRoute from '@composables/useOverlayAsRoute';
    import useEventBus from '@composables/useEventBus';
    import useClient from '@composables/useClient';
    import { useRouter } from 'vue-router';

    interface Props {
        contactId: string;
        leadId: string;
    }

    interface FormData {
        orderType: { value: string, label: string };
        assignedToTeam: Team | null;
        assignedToUser: User | null;
    }

    const orderTypeOptions = [
        { value: 'purchase', label: 'Aankoop' },
        { value: 'sale', label: 'Verkoop' },
        { value: 'mortgage', label: 'Hypotheek' },
        { value: 'rental', label: 'Verhuur' },
    ];

    const { client, currentUser, currentLegacyCompany } = useClient();

    if (!currentUser.value || !currentLegacyCompany.value) {
        throw new Error('Not authenticated');
    }

    const availableOrderTypeOptions = computed(
        () => {
            const { enabledTypes } = currentLegacyCompany.value.orderSettings;

            return orderTypeOptions
                .filter((orderTypeOption) => enabledTypes.includes(orderTypeOption.value as OrderType));
        },
    );

    const eventBus = useEventBus();
    const router = useRouter();
    const createOrderModal = ref<ExposedOverlay>();
    const props = defineProps<Props>();
    const formData = ref<FormData>({
        orderType: availableOrderTypeOptions.value[0],
        assignedToTeam: null,
        assignedToUser: null,
    });

    /** aliases are because something is being exported wrongly in one-api-client */
    const formSchema = {
        assignedToTeam: object({
            id: TeamId.validationSchema as unknown as StringSchema,
        }),
        assignedToUser: object({
            id: UserId.validationSchema as unknown as StringSchema,
        }).nullable(),
    };

    const lead = await client.get(Lead, props.leadId);
    const pipeline = await client.get(Pipeline, lead.pipeline.id);
    const availableAssignedToUsers = ref<User[]>([]) as Ref<User[]>;

    const createLegacyOrder = async () => {
        if (!client.session.userId) {
            throw new Error('Niet ingelogd');
        }

        if (!formData.value?.assignedToTeam) {
            throw new Error('Geen toegewezen aan team gekozen');
        }

        const orderId = uuid();
        const requestData = ref<{ [key: string]: string | boolean | number | string[] }>({
            referral: createResourceIdentifier('referrals', lead.id.toString()),
            id: orderId,
            type: formData.value.orderType.value,
            assignedToBranch: createResourceIdentifier(
                'branches',
                formData.value.assignedToTeam.id.value,
            ),
            status: 'pending',
        });

        if (formData.value.assignedToUser) {
            requestData.value.assignedTo = createResourceIdentifier(
                'users',
                formData.value.assignedToUser.id.value,
            );

            // Add assigned user in involved users
            requestData.value.involvedUsers = [requestData.value.assignedTo];
        }

        await OrderAPI.create(requestData.value as any);
        eventBus.dispatch('resource-manipulated', 'Order');
    };

    const onSave = async (event: any) => {
        await createLegacyOrder();
        event.button.setState('completed');
        redirectToDetail();
    };

    const availableAssignedToTeams = computed(
        () => (
            [...pipeline.teams]
                .filter((team) => team.active)
                .sort((a, b) => a.name.localeCompare(b.name))
        ),
    );

    const redirectToDetail = () => {
        router.push({
            name: 'contactDetail',
            params: {
                contactId: props.contactId,
            },
        });
    };

    useOverlayAsRoute({
        overlay: createOrderModal,
        isSideSheet: false,
        redirectMethod: redirectToDetail,
    });

    watch(formData, async () => {
        if (!formData.value.assignedToTeam) {
            return;
        }

        const userCollection = await client.getSubResources<TeamSubResource, User>(
            formData.value.assignedToTeam as Team,
            User,
            'available-users',
        );

        availableAssignedToUsers.value = userCollection.data;
    }, { deep: true });
</script>
